/**
 * SchoolLandingPicker — sport and year selection step for school detail.
 *
 * Renders inside SchoolDetailPage when the URL is /schools/:slug with no
 * `sport` or `target_year` params. The user selects a sport tile and a year
 * pill; once both are chosen the CTA navigates to
 * /schools/:slug?sport=<X>&target_year=<Y>.
 *
 * Implements WI-29 AC — picker gate, sport tiles, year pills, CTA, focus
 * management, aria semantics, and all state variants (loading, single-sport
 * auto-select, zero-data, error, not-found).
 *
 * Design artifact: project/designs/29-school-sport-picker.md
 *
 * Key states:
 *  - loading     — skeleton tiles while contexts fetch is in-flight
 *  - default     — multiple sports with data; none pre-selected
 *  - single-auto — exactly one sport has data; that tile is pre-selected
 *  - zero-data   — all sports have has_data:false; CTA permanently disabled
 *  - error       — network/5xx; error banner + retry
 *  - not-found   — 404 from contexts endpoint; renders not-found UI
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { fetchSchoolContexts, SchoolContexts, SportContext } from '../api/school';
import { TARGET_YEAR_OPTIONS } from '../constants/years';
import { useAuth } from '../hooks/useAuth';
import { AppHeader } from './AppHeader';
import { Badge } from './ui/Badge';
import { ErrorBanner } from './ui/ErrorBanner';
import { SkeletonRow } from './ui/SkeletonRow';

// ── Sport icon mapping (emoji as progressive enhancement) ─────────────────────
const SPORT_EMOJI: Record<string, string> = {
  soccer_mens: '⚽',
  soccer_womens: '⚽',
  football_mens: '🏈',
};

function sportEmoji(slug: string): string {
  return SPORT_EMOJI[slug] ?? '🏟';
}

// ── Division display labels (mirrors SchoolDetailPage) ────────────────────────
const DIVISION_LABEL: Record<string, string> = {
  ncaa_d1: 'D1',
  ncaa_d2: 'D2',
  ncaa_d3: 'D3',
  naia: 'NAIA',
  njcaa: 'NJCAA',
};

// ── Props ─────────────────────────────────────────────────────────────────────

interface SchoolLandingPickerProps {
  slug: string;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function SchoolLandingPicker({ slug }: SchoolLandingPickerProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loading: authLoading } = useAuth();
  const isAuthenticated = !!user && !authLoading;

  // ── Fetch state ──────────────────────────────────────────────────────────────
  const [contexts, setContexts] = useState<SchoolContexts | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ── Selection state ──────────────────────────────────────────────────────────
  const [selectedSport, setSelectedSport] = useState<string | null>(null);
  const [selectedYear, setSelectedYear] = useState<number | null>(null);

  // ── Focus ref for heading on mount ───────────────────────────────────────────
  const headingRef = useRef<HTMLHeadingElement>(null);

  // ── Live region ref for CTA enablement announcement ─────────────────────────
  const liveRef = useRef<HTMLSpanElement>(null);

  // ── Load contexts ────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setNotFound(false);
    try {
      const data = await fetchSchoolContexts(slug);
      if (data === null) {
        setNotFound(true);
        return;
      }
      setContexts(data);

      // Single-sport auto-select (OQ-29-3): if exactly one sport has data,
      // pre-select it on mount.
      const availableSports = data.sports.filter((s) => s.has_data);
      if (availableSports.length === 1) {
        setSelectedSport(availableSports[0].slug);
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to load school information.'
      );
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    load();
  }, [load]);

  // ── Focus management on mount ────────────────────────────────────────────────
  // When the picker mounts (and is not loading), move focus to the school name
  // heading so keyboard and screen-reader users start at the top of the page,
  // not left at the typeahead input from the previous AppHeader state.
  useEffect(() => {
    if (!loading && (contexts || notFound || error)) {
      headingRef.current?.focus();
    }
  }, [loading, contexts, notFound, error]);

  // ── Live region: announce CTA enablement ─────────────────────────────────────
  useEffect(() => {
    if (!liveRef.current) return;
    if (selectedSport && selectedYear && contexts) {
      const sport = contexts.sports.find((s) => s.slug === selectedSport);
      const sportName = sport?.name ?? selectedSport;
      liveRef.current.textContent = `Selection complete. View ${contexts.school_name} ${sportName} scores.`;
    } else {
      liveRef.current.textContent = '';
    }
  }, [selectedSport, selectedYear, contexts]);

  // ── Back link logic (mirrors SchoolDetailPage) ────────────────────────────────
  const fromParam = searchParams.get('from');
  const isFromWatchlist = fromParam === 'watchlist';
  const backLinkLabel = isFromWatchlist ? 'Back to watchlist' : 'Back to results';
  const backLinkHref = (() => {
    if (isFromWatchlist) return '/watchlist';
    const reconstructed = new URLSearchParams(searchParams);
    reconstructed.delete('from');
    const qs = reconstructed.toString();
    return qs ? `/?${qs}` : '/';
  })();

  // ── Derived state ─────────────────────────────────────────────────────────────
  const allNoData =
    contexts !== null && contexts.sports.every((s) => !s.has_data);
  const ctaEnabled = selectedSport !== null && selectedYear !== null && !allNoData;

  // CTA label for accessibility
  const ctaLabel = (() => {
    if (!contexts) return 'View scores';
    if (!ctaEnabled) return `View ${contexts.school_name} scores`;
    const sport = contexts.sports.find((s) => s.slug === selectedSport);
    const sportName = sport?.name ?? '';
    return `View ${contexts.school_name} ${sportName} scores`;
  })();

  function handleCta() {
    if (!ctaEnabled || !slug) return;
    navigate(`/schools/${slug}?sport=${selectedSport}&target_year=${selectedYear}`);
  }

  // ── Render: not-found ──────────────────────────────────────────────────────────
  if (notFound) {
    return (
      <div className="min-h-screen bg-surface-base">
        <AppHeader isAuthenticated={isAuthenticated} />
        <main className="max-w-3xl mx-auto px-4 py-12 text-center">
          <h1
            ref={headingRef}
            tabIndex={-1}
            className="text-2xl font-bold text-gray-900 mb-2 focus:outline-none"
          >
            We couldn&apos;t find that school.
          </h1>
          <p className="text-gray-500 mb-6">
            It may not be in our database yet. Try searching for another program.
          </p>
          <Link
            to="/"
            className="inline-block bg-brand-primary text-white px-5 py-2.5 rounded-lg hover:bg-brand-primary-dk motion-safe:transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2"
          >
            Back to search
          </Link>
        </main>
      </div>
    );
  }

  // ── Render: error ──────────────────────────────────────────────────────────────
  if (error) {
    return (
      <div className="min-h-screen bg-surface-base">
        <AppHeader isAuthenticated={isAuthenticated} />
        <main className="max-w-3xl mx-auto px-4 py-8">
          <ErrorBanner
            message={error}
            onDismiss={() => {
              setError(null);
              load();
            }}
          />
          <p className="mt-3 text-sm text-gray-600">
            <button
              type="button"
              onClick={load}
              className="text-brand-primary hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary rounded"
            >
              Try again
            </button>
          </p>
        </main>
      </div>
    );
  }

  // ── Render: loading skeleton ───────────────────────────────────────────────────
  if (loading || !contexts) {
    return (
      <div className="min-h-screen bg-surface-base">
        <AppHeader isAuthenticated={isAuthenticated} />
        <main
          className="max-w-3xl mx-auto px-4 py-6 space-y-6"
          aria-busy="true"
          aria-label="Loading school sport picker"
        >
          <SkeletonRow height="h-8" className="w-3/4" />
          <SkeletonRow height="h-4" className="w-1/2" />
          <p className="text-base font-semibold text-gray-900 mt-6 mb-3">Which sport?</p>
          <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
            <SkeletonRow height="h-[72px]" className="sm:w-52 rounded-lg" />
            <SkeletonRow height="h-[72px]" className="sm:w-52 rounded-lg" />
            <SkeletonRow height="h-[72px]" className="sm:w-52 rounded-lg" />
          </div>
        </main>
      </div>
    );
  }

  // ── Render: picker ─────────────────────────────────────────────────────────────
  const divisionBadgeLabel =
    DIVISION_LABEL[contexts.division] ?? contexts.division;

  return (
    <div className="min-h-screen bg-surface-base pb-24 md:pb-0">
      <AppHeader isAuthenticated={isAuthenticated} />

      {/* sr-only live region for CTA enablement announcement */}
      <span
        ref={liveRef}
        aria-live="polite"
        id="picker-status"
        className="sr-only"
      />

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-6">

        {/* Back link */}
        <Link
          to={backLinkHref}
          aria-label={backLinkLabel}
          className="min-h-[44px] inline-flex items-center gap-1 text-xs font-medium text-gray-600 hover:text-gray-800 motion-safe:transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 rounded"
        >
          ← {backLinkLabel}
        </Link>

        {/* School header */}
        <section>
          <h1
            ref={headingRef}
            tabIndex={-1}
            className="text-2xl font-bold text-gray-900 leading-tight focus:outline-none"
          >
            {contexts.school_name}
          </h1>
          <p className="text-xs text-gray-600 mt-1 flex items-center gap-1.5 flex-wrap">
            <Badge>{divisionBadgeLabel}</Badge>
            <span>{contexts.region} &middot; {contexts.city}, {contexts.state}</span>
          </p>
        </section>

        {/* Sport selection — radio group for accessibility */}
        <section aria-labelledby="sport-section-label">
          <p
            id="sport-section-label"
            aria-hidden="true"
            className="text-base font-semibold text-gray-900 mt-6 mb-3"
          >
            Which sport would you like to view?
          </p>

          <fieldset>
            <legend className="sr-only">Which sport would you like to view?</legend>
            <div
              role="radiogroup"
              aria-label="Sport selection"
              className="flex flex-col gap-3 sm:flex-row sm:flex-wrap"
            >
              {contexts.sports.map((sport: SportContext) => {
                const isSelected = selectedSport === sport.slug;
                const isDisabled = !sport.has_data;

                return (
                  <label
                    key={sport.slug}
                    className={[
                      'relative flex items-start gap-3 rounded-lg border p-4 cursor-pointer',
                      'sm:w-52 min-h-[60px] sm:min-h-[72px]',
                      // Layout: full-width on mobile, ~220px on desktop
                      'w-full',
                      isDisabled
                        ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-70'
                        : isSelected
                        ? 'border-brand-primary ring-2 ring-brand-primary bg-green-50'
                        : 'border-gray-200 bg-white hover:border-brand-primary hover:bg-brand-light',
                      'motion-safe:transition-colors',
                    ].join(' ')}
                  >
                    <input
                      type="radio"
                      name="sport"
                      value={sport.slug}
                      checked={isSelected}
                      disabled={isDisabled}
                      aria-disabled={isDisabled}
                      onChange={() => {
                        if (!isDisabled) setSelectedSport(sport.slug);
                      }}
                      className="sr-only"
                    />
                    {/* Sport icon */}
                    <span aria-hidden="true" className="text-xl mt-0.5 flex-shrink-0">
                      {sportEmoji(sport.slug)}
                    </span>
                    {/* Sport info */}
                    <div className="flex flex-col min-w-0">
                      <span
                        className={`text-sm font-semibold ${
                          isDisabled ? 'text-gray-400' : 'text-gray-900'
                        }`}
                      >
                        {sport.name}
                        {isSelected && (
                          <span className="ml-1" aria-hidden="true">
                            ✓
                          </span>
                        )}
                      </span>
                      <span
                        className={`text-xs mt-0.5 ${
                          isDisabled ? 'text-gray-400' : 'text-green-700'
                        }`}
                      >
                        {isDisabled ? 'Coming soon' : 'Data available'}
                      </span>
                    </div>
                  </label>
                );
              })}
            </div>
          </fieldset>
        </section>

        {/* Zero-data notice */}
        {allNoData && (
          <div
            role="status"
            className="bg-amber-50 border border-amber-300 rounded p-3 text-sm text-amber-800"
          >
            No scoring data available yet — check back after our next weekly scrape.
          </div>
        )}

        {/* Year selection */}
        <section aria-labelledby="year-section-label">
          <p
            id="year-section-label"
            className="text-base font-semibold text-gray-900 mt-6 mb-3"
          >
            Which year are you graduating?
          </p>
          <div className="flex flex-wrap gap-2" role="group" aria-labelledby="year-section-label">
            {TARGET_YEAR_OPTIONS.map((year) => {
              const isYearSelected = selectedYear === year;
              return (
                <button
                  key={year}
                  type="button"
                  aria-pressed={isYearSelected}
                  onClick={() => setSelectedYear(year)}
                  className={[
                    'px-2.5 py-0.5 text-xs rounded-full border min-h-[44px] min-w-[44px]',
                    'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-1',
                    'motion-safe:transition-colors',
                    isYearSelected
                      ? 'bg-brand-primary border-brand-primary text-white'
                      : 'border-gray-300 bg-white text-gray-700 hover:border-brand-primary',
                  ].join(' ')}
                >
                  {year}
                </button>
              );
            })}
          </div>
        </section>

        {/* CTA */}
        <div className="mt-6">
          <button
            type="button"
            disabled={!ctaEnabled}
            aria-label={ctaLabel}
            onClick={handleCta}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && ctaEnabled) handleCta();
            }}
            className={[
              'w-full sm:w-auto sm:min-w-[200px] px-6 py-3 text-base rounded-lg font-semibold',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2',
              'motion-safe:transition-colors',
              ctaEnabled
                ? 'bg-brand-primary text-white hover:bg-brand-primary-dk cursor-pointer'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed',
            ].join(' ')}
          >
            {ctaEnabled && contexts
              ? (() => {
                  const sport = contexts.sports.find((s) => s.slug === selectedSport);
                  return `View ${contexts.school_name} ${sport?.name ?? ''} scores`;
                })()
              : 'View scores'}
          </button>
        </div>

      </main>
    </div>
  );
}
